// Segment page — press groups. Replaces /pour/groupes-presse stub with
// use-case grid (regional/specialist/B2B), compliance emphasis (RGPD,
// audit log, editorial moderation, AI kill-switch), the "50 sites"
// architecture diagram, logo strip placeholder, and Enterprise-only CTA.

import { useTranslation } from "react-i18next";
import { Building2, BookOpen, Briefcase, ShieldCheck, FileSearch, Users, PowerOff } from "lucide-react";
import { Section } from "@/components/marketing/Section";
import { Eyebrow } from "@/components/marketing/Eyebrow";
import { DisplayHeading } from "@/components/marketing/DisplayHeading";
import { Pullquote } from "@/components/marketing/Pullquote";
import { LogoStrip } from "@/components/marketing/LogoStrip";
import { Rule } from "@/components/marketing/Rule";
import { MarketingSegmentLayout } from "@/components/marketing/MarketingSegmentLayout";
import { ArchitectureDiagram } from "@/components/marketing/ArchitectureDiagram";

const UTM_CAMPAIGN = "groupes-presse";

const USE_CASES = [
  { key: "regional", Icon: Building2 },
  { key: "specialist", Icon: BookOpen },
  { key: "b2b", Icon: Briefcase },
] as const;

const COMPLIANCE_ITEMS = [
  { key: "rgpd", Icon: ShieldCheck },
  { key: "auditLog", Icon: FileSearch },
  { key: "editorialModeration", Icon: Users },
  { key: "aiKillSwitch", Icon: PowerOff },
] as const;

export default function MediaGroupsPage() {
  const { t } = useTranslation();

  const pains = [0, 1, 2].map((i) => ({
    title: t(`marketing.pages.mediaGroups.pains.${i}.title`),
    body: t(`marketing.pages.mediaGroups.pains.${i}.body`),
  }));
  const solutions = [0, 1, 2].map((i) => ({
    title: t(`marketing.pages.mediaGroups.solutions.${i}.title`),
    body: t(`marketing.pages.mediaGroups.solutions.${i}.body`),
  }));

  return (
    <MarketingSegmentLayout
      seoTitle={t("marketing.pages.mediaGroups.seo.title")}
      seoDescription={t("marketing.pages.mediaGroups.seo.description")}
      path="/pour/groupes-presse"
      breadcrumbName={t("marketing.header.personas.pressGroups.title")}
      eyebrow={t("marketing.pages.mediaGroups.hero.eyebrow")}
      heroTitle={t("marketing.pages.mediaGroups.hero.title")}
      heroLead={t("marketing.pages.mediaGroups.hero.lead")}
      pains={pains}
      painsHeading={t("marketing.pages.mediaGroups.painsHeading")}
      solutions={solutions}
      solutionsHeading={t("marketing.pages.mediaGroups.solutionsHeading")}
      utmCampaign={UTM_CAMPAIGN}
      ctaHeadline={t("marketing.pages.mediaGroups.cta.headline")}
      ctaSubhead={t("marketing.pages.mediaGroups.cta.subhead")}
      primaryCtaLabel={t("marketing.pages.mediaGroups.cta.primary")}
      primaryCtaTo="/contact?subject=groupe-presse"
      secondaryCtaLabel={t("marketing.cta.bookDemo")}
      secondaryCtaTo="/contact?subject=demo-groupe"
    >
      <Rule />

      {/* Use cases — 3 verticals */}
      <Section padding="md">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.pages.mediaGroups.useCases.eyebrow")}</Eyebrow>
          <DisplayHeading level={2} className="mt-4">
            {t("marketing.pages.mediaGroups.useCases.title")}
          </DisplayHeading>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {USE_CASES.map(({ key, Icon }) => (
            <article key={key} className="border border-rule p-6">
              <Icon className="h-5 w-5 text-accent-press" aria-hidden />
              <h3 className="mt-4 text-base font-medium tracking-tight">
                {t(`marketing.pages.mediaGroups.useCases.items.${key}.title`)}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {t(`marketing.pages.mediaGroups.useCases.items.${key}.body`)}
              </p>
            </article>
          ))}
        </div>
      </Section>

      <Rule />

      {/* Compliance emphasis */}
      <Section padding="md" tone="ink">
        <div className="max-w-3xl">
          <Eyebrow className="text-paper/70">{t("marketing.pages.mediaGroups.compliance.eyebrow")}</Eyebrow>
          <DisplayHeading level={2} className="mt-4 text-paper">
            {t("marketing.pages.mediaGroups.compliance.title")}
          </DisplayHeading>
          <p className="mt-4 text-base leading-relaxed text-paper/70 md:text-lg">
            {t("marketing.pages.mediaGroups.compliance.lead")}
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          {COMPLIANCE_ITEMS.map(({ key, Icon }) => (
            <article key={key} className="border border-paper/20 p-6">
              <Icon className="h-5 w-5 text-paper" aria-hidden />
              <h3 className="mt-4 text-base font-medium tracking-tight text-paper">
                {t(`marketing.pages.mediaGroups.compliance.items.${key}.title`)}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-paper/70">
                {t(`marketing.pages.mediaGroups.compliance.items.${key}.body`)}
              </p>
            </article>
          ))}
        </div>
      </Section>

      {/* Architecture diagram */}
      <Section padding="md">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.pages.mediaGroups.architecture.eyebrow")}</Eyebrow>
          <DisplayHeading level={2} className="mt-4">
            {t("marketing.pages.mediaGroups.architecture.title")}
          </DisplayHeading>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            {t("marketing.pages.mediaGroups.architecture.lead")}
          </p>
        </div>
        <div className="mt-8 border border-rule bg-paper p-6 text-ink">
          <ArchitectureDiagram
            className="h-auto w-full text-ink"
            labels={{
              title: t("marketing.pages.mediaGroups.architecture.title"),
              groupConsole: t("marketing.pages.mediaGroups.architecture.diagram.groupConsole"),
              region: t("marketing.pages.mediaGroups.architecture.diagram.region"),
              sites: t("marketing.pages.mediaGroups.architecture.diagram.sites"),
              audit: t("marketing.pages.mediaGroups.architecture.diagram.audit"),
              rgpd: t("marketing.pages.mediaGroups.architecture.diagram.rgpd"),
            }}
          />
        </div>
      </Section>

      {/* Testimonial pullquote (placeholder) */}
      <Section padding="md" tone="paper">
        <Pullquote
          quote={t("marketing.pages.mediaGroups.testimonial.quote")}
          attribution={t("marketing.pages.mediaGroups.testimonial.attribution")}
        />
      </Section>

      {/* Logo strip — placeholder until real partner logos */}
      <Section padding="sm">
        <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {t("marketing.pages.mediaGroups.logoStrip.heading")}
        </h2>
        <div className="mt-6">
          <LogoStrip
            items={(t("marketing.pages.mediaGroups.logoStrip.items", { returnObjects: true }) as string[]).map((name) => ({ name }))}
          />
        </div>
      </Section>
    </MarketingSegmentLayout>
  );
}
